import { FC, useEffect, useState } from 'react';
import { Layout, Menu } from 'antd';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';

import { useIdleTimer } from 'react-idle-timer';
import useModal from '@hooks/useModal';
import GlobalStateContainer from '@containers/GlobalStateContainer';
import routeNames from '@modules/router/src/routeNames';
import TimeOutModal from '../../../modalHouse/modals/shared/TimeOutModal';
import TaggedLink from '../../../components/TaggedLink';
import layoutRouteProps from '../src/layoutRouteProps';
import HeaderContent from './HeaderContent';
import RightContent from './RightContent';

import Logo from '../../../assets/logo-32x32.png';

const { Header, Sider, Content } = Layout;

const AdminLayout: FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { cookies, removeAllCookies } = GlobalStateContainer.useContainer();

  const findParentKeys = (routes: any[], path: string, parents: string[] = []): string[] => {
    for (const route of routes) {
      if (route.path === path) {
        return parents;
      }
      if (route.children && route.children.length > 0) {
        const found = findParentKeys(route.children, path, [...parents, route.name]);
        if (found.length > 0 || route.children.some((child: any) => child.path === path)) {
          return found.length > 0 ? found : [...parents, route.name];
        }
      }
    }
    return [];
  };

  useEffect(() => {
    const parentKeys = findParentKeys(layoutRouteProps.children || [], pathname);
    if (parentKeys.length > 0) {
      setOpenKeys(parentKeys);
    }
  }, [pathname]);

  const { showModal, openModal, closeModal } = useModal();

  const onIdle = () => {
    removeAllCookies();
    openModal();
  };

  useIdleTimer({ onIdle, timeout: 30 * 60 * 1000 }); // 30 mins

  useEffect(() => {
    const accessTokenValid = !!cookies.accessToken;
    const tokenTypeValid = !!cookies.tokenType;
    const refreshTokenValid = !!cookies.refreshToken;

    if (!accessTokenValid || !tokenTypeValid || !refreshTokenValid) {
      navigate(routeNames.home);
    }
  }, [cookies.accessToken, cookies.tokenType, cookies.refreshToken, navigate]);

  const generateMenuItems = (routes: any[]): any[] => {
    return routes.map((route) => {
      if (route.children && route.children.length > 0) {
        return {
          key: route.name,
          icon: route.icon,
          label: route.name,
          children: generateMenuItems(route.children),
        };
      }

      return {
        key: route.path,
        icon: route.icon,
        label: (
          <TaggedLink to={route.path || '/'} tag={route.path || '/'}>
            {route.name || route.path}
          </TaggedLink>
        ),
      };
    });
  };

  const menuItems = generateMenuItems(layoutRouteProps.children || []);

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Layout className="h-screen" hasSider>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={250}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >

        <div
          style={{
            height: 32,
            margin: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            overflow: 'hidden',
          }}
        >
          <img src={Logo} alt="logo" width={32} height={32} />
          {!collapsed && <span style={{ color: 'white', fontSize: 18, fontWeight: 600 }}>P2P Admin</span>}
        </div>

        <Menu theme="dark" mode="inline" selectedKeys={[pathname]} openKeys={openKeys} onOpenChange={onOpenChange} items={menuItems} />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <HeaderContent collapsed={collapsed} toggleCollapsed={setCollapsed} />
          <RightContent />
        </Header>

        <Content>
          <Outlet />
        </Content>
      </Layout>

      <TimeOutModal showModal={showModal} closeModal={closeModal} />
    </Layout>
  );
};

export default AdminLayout;
